import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Sub-viewport render targets by node path, the seam between an offscreen
 * `SubViewport` and what displays it (ADR-0033). The map is a store, not a context value, since
 * every Texture2D slot reads it and a publish re-renders only the readers of its path. Both work
 * without a provider.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { createExternalStore, useExternalStoreSelection, useExternalStoreValue, } from '../hooks/createExternalStore.js';
const EMPTY = new Map();
/** Without a provider, a publish sets nothing and every read finds no target. */
const NO_PROVIDER = {
    get: () => EMPTY,
    set: () => { },
    subscribe: () => () => { },
};
/** Written only by `registerTarget`, through a provider's own store. */
const TargetsContext = createContext(NO_PROVIDER);
TargetsContext.displayName = 'ViewportTexturesContext';
/**
 * Publishes `entry` at `path`, the one key a `viewport_path` walks to: a consumer
 * resolves a `%Name` through its claim table to the claimant's own path. One hook
 * serves every publisher, so a consumer never knows which host produced its target.
 */
export function usePublishViewportTexture(path, entry) {
    const targets = useContext(TargetsContext);
    useEffect(() => registerTarget(targets, path, entry), [targets, path, entry]);
}
/** Sets `entry` at `path` in `targets`, and returns the cleanup. */
function registerTarget(targets, path, entry) {
    targets.set(new Map(targets.get()).set(path, entry));
    return () => {
        // Only this exact entry, so a remount that re-registered first survives.
        if (targets.get().get(path) !== entry)
            return;
        const next = new Map(targets.get());
        next.delete(path);
        targets.set(next);
    };
}
/** The target published at `path`, or null. A reader of no path never re-renders. */
export function useViewportTexture(path) {
    return useExternalStoreSelection(useContext(TargetsContext), (targets) => path === null ? null : (targets.get(path) ?? null));
}
/** Every published target, by path, for a reader that resolves many paths outside a hook. */
export function useViewportTextures() {
    return useExternalStoreValue(useContext(TargetsContext));
}
export function ViewportTextureProvider({ children }) {
    const [targets] = useState(() => createExternalStore(EMPTY));
    return _jsx(TargetsContext.Provider, { value: targets, children: children });
}
//# sourceMappingURL=ViewportTextureContext.js.map